import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Accordion, Card, Spinner } from "react-bootstrap";
import styled from "styled-components";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getPatients,
  loadOptionPatients,
  loadSelectedPatient,
} from "../../../apis/patients";
import { getUnconsumableReport } from "../../../apis/customTemplate";
import { toastify } from "../../../helperFunctions/toastify";
import { setTableSearch } from "../../../redux/slices/commonSlice";
import { formatDate, downloadCsv } from "../../../helperFunctions/utils";
import AsyncSelect from "../../SharedComponents/AsyncSelect";

const FilterWrapper = styled.div`
  label {
    color: inherit;
  }

  .btn-wrapper {
    align-items: center;
    display: flex;
    margin-top: 20px;
  }
`;

// Helper to format numbers with comma separator
const formatNumber = (value) => {
  if (value === undefined || value === null || isNaN(value)) return "0.00";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function UnconsumableReport() {
  const dispatch = useDispatch();
  const location = useLocation();
  const buttonRef = useRef(null);
  const pathname = location.pathname;
  const fieldPathname = pathname.replace("/", "");
  const { tableSearch } = useSelector((state) => state.commonData);

  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [refetch, setRefetch] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch patients
  useEffect(() => {
    getPatients().then((res) => {
      if (res?.data) setPatients(res.data);
    });
  }, []);

  // Fetch report data (only by patient)
  useEffect(() => {
    setLoading(true);
    getUnconsumableReport({
      patient: selectedPatient?._id || null,
    })
      .then((res) => {
        if (res?.result?.data) setFilteredData(res.result.data);
        else setFilteredData([]);
      })
      .finally(() => setLoading(false));
  }, [refetch]);

  const onReduxFilter = (valuePatient) => {
    dispatch(
      setTableSearch({
        ...tableSearch,
        [fieldPathname]: {
          ...tableSearch[fieldPathname],
          [tableSearch[fieldPathname]?.currentState]: {
            selectedPatient: valuePatient,
          },
        },
      })
    );
  };

  const exportCsv = () => {
    const csvData = [];

    filteredData.forEach((patient) => {
      patient.packages?.forEach((pkg) => {
        // Add header row for each package (includes patient + package info)
        csvData.push({
          "Patient Name": patient.patientName,
          "Package Name": pkg.packageName + (pkg.isExpired ? " (Expired)" : ""),
          "Purchase Date": formatDate(pkg.purchaseDate),
          Type: "",
          Name: "",
          "Total Amount": "",
          "Consumed Amount": "",
          "Balance Qty": "",
          "Expired Amount": "",
        });

        // Combine products + treatments into same list for export
        const combinedItems = [
          ...(pkg.products || []).map((p) => ({
            Type: "Product",
            Name: p.name,
            TotalAmount: (p.qty || 0) * (p.price || 0),
            ConsumedAmount: p.usedAmount || 0,
            BalanceQty: p.isExpired ? 0 : p.remainingQty || 0,
            ExpiredAmount: p.expiredAmount || 0,
          })),
          ...(pkg.treatments || []).map((t) => ({
            Type: "Treatment",
            Name: t.name,
            TotalAmount: (t.qty || 0) * (t.price || 0),
            ConsumedAmount: t.usedAmount || 0,
            BalanceQty: t.remainingQty || 0,
            ExpiredAmount: " ",
          })),
        ];

        combinedItems.forEach((item) => {
          csvData.push({
            "Patient Name": "",
            "Package Name": "",
            "Purchase Date": "",
            Type: item.Type,
            Name: item.Name,
            "Total Amount": item.TotalAmount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
            "Consumed Amount": item.ConsumedAmount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
            "Balance Qty": item.BalanceQty.toLocaleString(),
            "Expired Amount": item.ExpiredAmount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          });
        });

        // Empty line between packages for readability
        csvData.push({
          "Patient Name": "",
          "Package Name": "",
          "Purchase Date": "",
          Type: "",
          Name: "",
          "Total Amount": "",
          "Consumed Amount": "",
          "Balance Qty": "",
          "Expired Amount": "",
        });
      });
    });

    downloadCsv(csvData, "Unconsumable_Report");
  };

  return (
    <div className="p-0">
      <div className="mainHeading" style={{ paddingTop: "0px" }}>
        <FilterWrapper
          className="d-flex flex-wrap align-items-center"
          style={{ gap: "12px" }}
        >
          <Form.Group style={{ width: "260px" }}>
            <Form.Label className="headings2" style={{ color: "#000" }}>
              Filter by Patient
            </Form.Label>
            <AsyncSelect
              value={selectedPatient}
              setValue={(value) => setSelectedPatient(value)}
              loadOptions={(search, loadedOptions, { page }) =>
                loadOptionPatients(search, loadedOptions, { page })
              }
              additional={{ page: 1 }}
              placeholder="Search Patient..."
              defaultOptions
              cacheOptions
            />
          </Form.Group>

          <div className="btn-wrapper d-flex" style={{ gap: "10px" }}>
            <Button
              ref={buttonRef}
              className="addItem"
              onClick={() => {
                onReduxFilter(selectedPatient);
                setRefetch(!refetch);
              }}
            >
              Search
            </Button>

            <Button className="addItem" onClick={exportCsv}>
              Export
            </Button>
          </div>
        </FilterWrapper>
      </div>

      <Accordion
        defaultActiveKey="0"
        alwaysOpen
        style={{
          maxHeight: filteredData?.length > 15 ? "800px" : "auto",
          overflowY: filteredData?.length > 15 ? "auto" : "visible",
        }}
      >
        {loading ? (
          <div className="text-center mt-4">
            <Spinner animation="border" size="sm" /> Loading report...
          </div>
        ) : filteredData?.length > 0 ? (
          filteredData.map((patient, index) => (
            <Accordion.Item eventKey={index.toString()} key={index}>
              <Accordion.Header>
                <strong>{patient?.patientName}</strong>
                {patient?.patientEmail && <> — {patient.patientEmail}</>}
              </Accordion.Header>

              <Accordion.Body>
                {patient?.packages
                  ?.slice()
                  .reverse()
                  .map((pkg, i) => (
                    <Card key={i} className="mb-3">
                      <Card.Body>
                        <h6>
                          <strong>{pkg?.packageName}</strong>{" "}
                          {pkg?.isExpired && (
                            <span style={{ color: "red" }}> (Expired)</span>
                          )}
                        </h6>

                        <p>
                          Purchased On: {formatDate(pkg?.purchaseDate)} | Qty:{" "}
                          {pkg?.totalQty}
                        </p>
                        <p>
                          <strong>Unit Price:</strong>{" "}
                          {formatNumber(pkg?.totalPackageUnitPrice)} |{" "}
                          <strong>Total Price:</strong>{" "}
                          {formatNumber(pkg?.totalPackagePrice)} |{" "}
                          <strong>Consumed:</strong>{" "}
                          {formatNumber(pkg?.totalUsedAmount)} |{" "}
                          <strong>Unconsumed:</strong>{" "}
                          {formatNumber(pkg?.totalUnconsumedAmount)} |{" "}
                          <strong>Expired Amount:</strong>{" "}
                          {formatNumber(pkg?.totalExpiredAmount)}
                        </p>

                        {pkg?.products?.length > 0 && (
                          <>
                            <h6>Products</h6>
                            <DataTable
                              columns={[
                                { name: "Name", selector: (r) => r?.name },
                                {
                                  name: "Total Qty",
                                  selector: (r) => r?.qty || 0,
                                },
                                {
                                  name: "Total Amount",
                                  selector: (r) =>
                                    formatNumber(
                                      (r?.qty || 0) * (r?.price || 0)
                                    ),
                                },
                                {
                                  name: "Consumed Amount",
                                  selector: (r) =>
                                    formatNumber(r?.usedAmount || 0),
                                },
                                {
                                  name: "Expired Amount",
                                  selector: (r) =>
                                    formatNumber(r?.expiredAmount || 0),
                                },

                                {
                                  name: "Balance Amount",
                                  selector: (r) =>
                                    r?.isExpired
                                      ? 0
                                      : r?.remainingQty * r?.price || 0,
                                },
                              ]}
                              data={pkg?.products}
                              dense
                              noHeader
                            />
                          </>
                        )}

                        {pkg?.treatments?.length > 0 && (
                          <>
                            <h6 className="mt-3">Treatments</h6>
                            <DataTable
                              columns={[
                                { name: "Name", selector: (r) => r?.name },
                                {
                                  name: "Total Qty",
                                  selector: (r) => r?.qty || 0,
                                },
                                {
                                  name: "Total Amount",
                                  selector: (r) =>
                                    formatNumber(
                                      (r?.qty || 0) * (r?.price || 0)
                                    ),
                                },
                                {
                                  name: "Consumed Amount",
                                  selector: (r) =>
                                    formatNumber(r?.usedAmount || 0),
                                },

                                {
                                  name: "Balance Amount",
                                  selector: (r) =>
                                    r?.remainingQty * r?.price || 0,
                                },
                              ]}
                              data={pkg?.treatments}
                              dense
                              noHeader
                            />
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
              </Accordion.Body>
            </Accordion.Item>
          ))
        ) : (
          <div className="text-center mt-4">No records found</div>
        )}
      </Accordion>
    </div>
  );
}

export default UnconsumableReport;
